import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Building,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  DollarSign,
  TrendingUp,
  Calendar,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Briefcase
} from 'lucide-react';
import { suppliersService } from '../services/suppliersService';
import { entriesService } from '../services/entriesService';

const SupplierDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [newContact, setNewContact] = useState({
    contact_person: '',
    phone: '',
    email: '',
    position: '',
    is_primary: false
  });

  useEffect(() => {
    if (id) {
      loadSupplierData();
    }
  }, [id]);

  const loadSupplierData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Cargar datos del proveedor
      const supplierData = await suppliersService.getById(id);
      setSupplier(supplierData);

      // Cargar contactos del proveedor
      const contactsData = await suppliersService.getContacts(id);
      setContacts(contactsData);

      // Cargar entradas del proveedor
      const entriesData = await entriesService.getBySupplierId(id);
      setEntries(entriesData);

    } catch (error) {
      console.error('Error cargando datos del proveedor:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getTotalEntries = () => {
    // En lugar de sumar cantidades, contamos el número total de productos en todas las entradas
    return entries.reduce((total, entry) => {
      // Si la entrada tiene products, sumamos la cantidad de productos
      if (entry.products && Array.isArray(entry.products)) {
        return total + entry.products.length;
      }
      // Si la entrada tiene product_count, lo usamos
      if (typeof entry.product_count === 'number') {
        return total + entry.product_count;
      }
      // Si no hay información de productos, contamos como 1
      return total + 1;
    }, 0);
  };

  const getTotalPaid = () => {
    // Suma los costos totales verificando que sean números válidos
    return entries.reduce((total, entry) => {
      // Si el costo total no existe o no es un número, usamos 0
      const cost = typeof entry.total_cost === 'number' ? entry.total_cost : 
                  (entry.total_cost ? parseFloat(entry.total_cost) : 0);
      // Verifica que el resultado sea un número válido
      return total + (isNaN(cost) ? 0 : cost);
    }, 0);
  };

  const formatMoney = (amount) => {
    // Asegurarse de que amount sea un número
    const safeAmount = typeof amount === 'number' ? amount : 
                       (amount ? parseFloat(amount) : 0);
    
    // Si no es un número válido después de la conversión, mostrar 0
    const finalAmount = isNaN(safeAmount) ? 0 : safeAmount;
    
    const formatter = new Intl.NumberFormat('es-MX', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      useGrouping: true
    });
    return `$${formatter.format(finalAmount)} MXN`;
  };

  const handleSaveContact = async () => {
    try {
      if (editingContact) {
        // Actualizar contacto existente
        await suppliersService.updateContact(id, editingContact.id, newContact);
      } else {
        // Crear nuevo contacto
        await suppliersService.createContact(id, newContact);
      }
      
      // Recargar contactos
      const contactsData = await suppliersService.getContacts(id);
      setContacts(contactsData);
      
      // Resetear formulario
      setShowContactForm(false);
      setEditingContact(null);
      setNewContact({
        contact_person: '',
        phone: '',
        email: '',
        position: '',
        is_primary: false
      });
    } catch (error) {
      console.error('Error guardando contacto:', error);
      alert('Error al guardar el contacto');
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setNewContact({
      contact_person: contact.contact_person || '',
      phone: contact.phone || '',
      email: contact.email || '',
      position: contact.position || '',
      is_primary: contact.is_primary || false
    });
    setShowContactForm(true);
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este contacto?')) {
      try {
        await suppliersService.deleteContact(id, contactId);
        const contactsData = await suppliersService.getContacts(id);
        setContacts(contactsData);
      } catch (error) {
        console.error('Error eliminando contacto:', error);
        alert('Error al eliminar el contacto');
      }
    }
  };

  const handleEdit = () => {
    // Navegar a la página de proveedores con modo edición
    navigate('/suppliers', { state: { editSupplier: supplier } });
  };

  const handleDelete = async () => {
    try {
      await suppliersService.delete(id);
      navigate('/suppliers');
    } catch (error) {
      console.error('❌ Error eliminando proveedor:', error);
      setError(error.message);
    }
  };

  const handleGoBack = () => {
    navigate('/suppliers');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Cargando información del proveedor...</p>
        </div>
      </div>
    );
  }

  if (error || !supplier) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <div className="text-red-600 text-xl mb-4">⚠️ Error</div>
          <p className="text-gray-700 mb-4">{error || 'Proveedor no encontrado'}</p>
          <button 
            onClick={() => navigate('/suppliers')} 
            className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
          >
            Volver a Proveedores
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header con botón de regreso y acciones */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={handleGoBack}
            className="flex items-center gap-2 text-green-600 hover:text-green-800 font-medium transition-colors duration-300"
          >
            <ArrowLeft className="w-5 h-5" />
            Volver a Proveedores
          </button>
          
          <div className="flex items-center gap-3">
            <motion.button
              onClick={handleEdit}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Edit className="w-4 h-4" />
              Editar
            </motion.button>
            <motion.button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Trash2 className="w-4 h-4" />
              Eliminar
            </motion.button>
          </div>
        </div>

        {/* Título principal */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-extrabold text-gray-800 mb-2 bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Información del Proveedor
          </h1>
          <p className="text-gray-600">Datos completos, contactos y movimientos</p>
        </motion.div>

        {/* Contenido principal en grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Columna izquierda - Información básica */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1 space-y-6"
          >
            {/* Información básica del proveedor */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                <Building className="w-6 h-6 text-green-600" />
                {supplier.name}
              </h2>

              <div className="space-y-4">
                {supplier.contact_person && (
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Contacto Principal</p>
                      <p className="font-semibold">{supplier.contact_person}</p>
                    </div>
                  </div>
                )}

                {supplier.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Teléfono</p>
                      <p className="font-semibold">{supplier.phone}</p>
                    </div>
                  </div>
                )}

                {supplier.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Email</p>
                      <p className="font-semibold">{supplier.email}</p>
                    </div>
                  </div>
                )}

                {supplier.address && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Dirección</p>
                      <p className="font-semibold">{supplier.address}</p>
                    </div>
                  </div>
                )}

                {supplier.website && (
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Sitio Web</p>
                      <a 
                        href={supplier.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-semibold text-green-600 hover:text-green-800"
                      >
                        {supplier.website}
                      </a>
                    </div>
                  </div>
                )}

                {supplier.created_at && (
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="text-sm text-gray-500">Registrado</p>
                      <p className="font-semibold">
                        {new Date(supplier.created_at).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Resumen de movimientos */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Resumen de Compras</h3>
              
              <div className="grid grid-cols-1 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-600 font-medium">Total Entradas</p>
                  <p className="text-2xl font-bold text-green-700">
                    {getTotalEntries().toLocaleString('es-MX')}
                  </p>
                  <p className="text-xs text-green-500">{entries.length} movimiento(s)</p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <DollarSign className="w-8 h-8 text-blue-600 mx-auto mb-2" />
                  <p className="text-sm text-blue-600 font-medium">Total Pagado</p>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatMoney(getTotalPaid())}
                  </p>
                  <p className="text-xs text-blue-500">Monto total de compras</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Columna derecha - Contactos y entradas */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Contactos */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">Contactos</h3>
                <button
                  onClick={() => setShowContactForm(true)}
                  className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors duration-300"
                >
                  <Plus className="w-4 h-4" />
                  Agregar Contacto
                </button>
              </div>

              {/* Lista de contactos */}
              <div className="space-y-3">
                {contacts.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay contactos registrados</p>
                ) : (
                  contacts.map(contact => (
                    <div key={contact.id} className="bg-gray-50 rounded-lg p-4 flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold text-gray-800">{contact.contact_person}</h4>
                          {contact.is_primary && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full">
                              Principal
                            </span>
                          )}
                        </div>
                        {contact.position && (
                          <p className="text-sm text-gray-600 mb-1">
                            <Briefcase className="w-4 h-4 inline mr-1" />
                            {contact.position}
                          </p>
                        )}
                        {contact.phone && (
                          <p className="text-sm text-gray-600 mb-1">
                            <Phone className="w-4 h-4 inline mr-1" />
                            {contact.phone}
                          </p>
                        )}
                        {contact.email && (
                          <p className="text-sm text-gray-600">
                            <Mail className="w-4 h-4 inline mr-1" />
                            {contact.email}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditContact(contact)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Historial de entradas */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 mb-6">Historial de Entradas</h3>
              
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {entries.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No hay entradas registradas</p>
                ) : (
                  entries.map(entry => (
                    <div 
                      key={entry.id} 
                      className="border-l-4 border-green-400 bg-green-50 p-4 rounded-r-lg cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => navigate(`/entries/entry/${entry.id}`)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          {/* Si es una entrada multi-producto, mostrar cantidad de productos */}
                          {entry.products && entry.products.length > 0 ? (
                            <>
                              <h4 className="font-semibold text-green-800">
                                <span className="bg-green-200 text-green-900 text-xs font-bold px-2 py-1 rounded-full mr-2">
                                  ID: {entry.id}
                                </span>
                                Entrada con {entry.products.length} producto(s)
                              </h4>
                              <p className="text-sm text-green-600">
                                Primer producto: {entry.products[0]?.product_name || 'No disponible'}
                              </p>
                            </>
                          ) : (
                            <>
                              <h4 className="font-semibold text-green-800">
                                <span className="bg-green-200 text-green-900 text-xs font-bold px-2 py-1 rounded-full mr-2">
                                  ID: {entry.id}
                                </span>
                                {entry.product_name || 'Entrada de inventario'}
                              </h4>
                              <p className="text-sm text-green-600">SKU: {entry.product_sku || 'No disponible'}</p>
                            </>
                          )}
                          
                          <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-gray-500">Productos:</span>
                              <span className="font-medium ml-1">
                                {entry.product_count || entry.products?.length || 1}
                              </span>
                            </div>
                            <div>
                              <span className="text-gray-500">Costo Total:</span>
                              <span className="font-medium ml-1">
                                {formatMoney(entry.total_cost || 0)}
                              </span>
                            </div>
                          </div>
                          {entry.invoice_number && (
                            <p className="text-xs text-gray-500 mt-1">
                              Factura: {entry.invoice_number}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-500">
                            {(() => {
                              const date = new Date(entry.entry_date);
                              const userTimezoneOffset = date.getTimezoneOffset() * 60000;
                              const localDate = new Date(date.getTime() + userTimezoneOffset);
                              return localDate.toLocaleDateString('es-ES');
                            })()}
                          </p>
                          <p className="text-xs text-gray-400">
                            {new Date(entry.created_at).toLocaleTimeString('es-ES')}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modal para agregar/editar contacto */}
      <AnimatePresence>
        {showContactForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  {editingContact ? 'Editar Contacto' : 'Nuevo Contacto'}
                </h3>
                <button
                  onClick={() => {
                    setShowContactForm(false);
                    setEditingContact(null);
                    setNewContact({
                      contact_person: '',
                      phone: '',
                      email: '',
                      position: '',
                      is_primary: false
                    });
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre del Contacto *
                  </label>
                  <input
                    type="text"
                    value={newContact.contact_person}
                    onChange={(e) => setNewContact({...newContact, contact_person: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ej: Juan Pérez"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cargo/Posición
                  </label>
                  <input
                    type="text"
                    value={newContact.position}
                    onChange={(e) => setNewContact({...newContact, position: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ej: Gerente de Ventas"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={newContact.phone}
                    onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ej: +52 55 1234 5678"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={newContact.email}
                    onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="Ej: juan@empresa.com"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_primary"
                    checked={newContact.is_primary}
                    onChange={(e) => setNewContact({...newContact, is_primary: e.target.checked})}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-700">
                    Contacto principal
                  </label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowContactForm(false);
                    setEditingContact(null);
                    setNewContact({
                      contact_person: '',
                      phone: '',
                      email: '',
                      position: '',
                      is_primary: false
                    });
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-400 transition-colors duration-300 flex items-center justify-center gap-2"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
                <button
                  onClick={handleSaveContact}
                  className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors duration-300 flex items-center justify-center gap-2"
                  disabled={!newContact.contact_person.trim()}
                >
                  <Save className="w-4 h-4" />
                  Guardar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal de Confirmación de Eliminación */}
      <AnimatePresence>
        {showDeleteModal && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4"
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
            >
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Confirmar Eliminación
              </h3>
              <p className="text-gray-600 mb-6">
                ¿Estás seguro de que quieres eliminar este proveedor? Esta acción no se puede deshacer y afectará todos los registros relacionados.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Eliminar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SupplierDetailPage;
